// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import React, { useEffect, useLayoutEffect, useRef } from 'react';

const STROKE_COLOR = '#86ea5c';
const STROKE_WIDTH = 2;

type Props = {
  /** [fromSiteId, toSiteId] edges to draw */
  edges: [string, string][];
  /** Mutable map of siteId → wrapper div element */
  cardRefs: Map<string, HTMLElement | null>;
  visible: boolean;
  /** Board container ref — SVG is clipped to this element's bounds */
  containerRef: React.RefObject<HTMLElement>;
};

/**
 * Renders a full-viewport fixed SVG overlay with cubic bezier lines connecting
 * highlighted cards. Uses imperative DOM updates (not React state) for
 * performance — lines redraw on every scroll without triggering React re-renders.
 *
 * Position strategy: position:fixed SVG + getBoundingClientRect() means lines
 * always draw to the card's actual visual position, including when cards are
 * position:sticky (pinned to column edges while scrolling).
 */
const TraceBoardLines: React.FC<Props> = ({
  edges,
  cardRefs,
  visible,
  containerRef,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const redraw = () => {
    const svg = svgRef.current;
    if (!svg) return;

    // Clip SVG to board container bounds so lines don't render over sidebar/header
    const container = containerRef.current;
    if (container) {
      const r = container.getBoundingClientRect();
      svg.style.clipPath = `inset(${r.top}px ${window.innerWidth - r.right}px ${
        window.innerHeight - r.bottom
      }px ${r.left}px)`;
    }

    // Clear old paths imperatively (faster than React reconcile on scroll)
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    if (!visible || !edges.length) return;

    // Count how many edges arrive at each destination so we can assign each
    // a different vertical "bow" and keep the arcs visually separated while
    // both endpoints stay at the exact card centres.
    const toCounts = new Map<string, number>();
    for (const [, toId] of edges) {
      toCounts.set(toId, (toCounts.get(toId) ?? 0) + 1);
    }
    const toIndex = new Map<string, number>();

    // Bow is expressed as a fraction of the horizontal distance (dx) instead of
    // a fixed pixel amount. Longer lines therefore bow more in absolute terms,
    // which naturally lifts/lowers them above/below intermediate departure points
    // without requiring extra knowledge of column positions.
    const BOW_FRACTION = 0.22;

    for (const [fromId, toId] of edges) {
      const fromEl = cardRefs.get(fromId);
      const toEl = cardRefs.get(toId);
      if (!fromEl || !toEl) continue;

      const fr = fromEl.getBoundingClientRect();
      const tr = toEl.getBoundingClientRect();

      // Skip cards with zero dimensions (not yet mounted / display:none)
      if (fr.width === 0 || tr.width === 0) continue;

      // Both endpoints are always the exact vertical centre of their card.
      const x1 = fr.right;
      const y1 = fr.top + fr.height / 2;
      const x2 = tr.left;
      const y2 = tr.top + tr.height / 2;

      const dx = Math.abs(x2 - x1);

      const toTotal = toCounts.get(toId) ?? 1;
      const tIdx = toIndex.get(toId) ?? 0;
      toIndex.set(toId, tIdx + 1);

      // Each line arriving at the same destination gets a distinct bow offset.
      // bow=0 when toTotal===1 (single line → clean S-curve, no deviation).
      // The offset is proportional to dx so a 2-column-wide line bows twice as
      // much as a 1-column-wide line, clearing intermediate departure points.
      const bow = (tIdx - (toTotal - 1) / 2) * BOW_FRACTION * dx;

      const cp1x = x1 + dx * 0.5;
      const cp2x = x2 - dx * 0.5;
      const cp1y = y1 + bow;
      const cp2y = y2 + bow;

      const path = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path'
      );
      path.setAttribute(
        'd',
        `M ${x1} ${y1} C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${x2} ${y2}`
      );
      path.setAttribute('stroke', STROKE_COLOR);
      path.setAttribute('stroke-width', String(STROKE_WIDTH));
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke-linecap', 'round');
      svg.appendChild(path);
    }
  };

  // Redraw after layout settles whenever edges or visibility changes
  useLayoutEffect(() => {
    redraw();
  });

  // Reattach scroll and resize listener when visibility/edges change
  useEffect(() => {
    if (!visible) return;

    let rafId: number;
    const onScrollOrResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(redraw);
    };

    window.addEventListener('scroll', onScrollOrResize, {
      capture: true,
      passive: true,
    });

    window.addEventListener('resize', onScrollOrResize, {
      capture: true,
      passive: true,
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
    };
  }, [visible, edges]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <svg
      ref={svgRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 200,
      }}
    />
  );
};

export default React.memo(TraceBoardLines);
